import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";

/**
 * Record Deposit File Step
 *
 * The middle of an "AR Deposit File Intake" run: reads what Extract Document
 * Content (V2) found on one file dropped into a check deposit (what kind of
 * file it is and, for a check, the fields on its face) and writes it onto the
 * deposit's row for the file. A file holding more than one check is flagged
 * (`holdsSeveralChecks`) for Split Document (V2) and Split Deposit File to
 * split it, one row per check. Once a check is recorded, the deposit's
 * checks are paired with its remittances again.
 *
 * Nothing is written once the deposit has been submitted.
 */
export const recordDepositFileStep = defineStepConfig({
  type: "record_deposit_file",
  name: "Record Deposit File",
  description:
    "Write what was read off a check deposit file (its kind and the check's fields) onto the deposit",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    depositBatchItemId: z
      .uuid()
      .describe("The deposit's row for the file (from the trigger)"),
    documentOcrResults: z
      .array(
        z.object({
          document: entityOfType(SystemEntity.CORE_DOCUMENT),
          ocrResult: entityOfType(
            SystemEntity.CORE_DOCUMENT_OCR_RESULT,
          ).optional(),
          status: z.enum(["completed", "failed", "skipped"]),
          error: z.string().optional(),
        }),
      )
      .describe(
        "Extract Document Content (V2)'s per-document results, read with the check schema",
      ),
  }),

  outputSchema: z.object({
    status: z
      .enum(["recorded", "failed", "skipped"])
      .describe(
        "recorded: the file's fields are on the deposit; failed: the file could not be used (the reason is on the deposit); skipped: the deposit was already submitted",
      ),
    kind: z
      .string()
      .nullable()
      .describe("check, remittance, check_with_remittance or other"),
    hasRemittance: z
      .boolean()
      .describe("Whether the file carries a remittance to submit"),
    holdsSeveralChecks: z
      .boolean()
      .describe(
        "Whether the file holds more than one check, and is to be split into one file per check",
      ),
    failureReason: z.string().nullable(),
    depositBatchItemId: z.uuid().describe("Pass-through of the file's row"),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "1 minute",
    retry: {
      maximumAttempts: 3,
      initialInterval: "5 seconds",
      backoffCoefficient: 2,
    },
  },
});
